import { MONTHS } from '../constants';
import { formatMonthUrl, parseCalendarUrl } from '../utils/date';
import { getMonthName, localizePath, t } from '../i18n';

class AppFooter extends HTMLElement {
  private pageType: 'main' | 'support' | 'printer' | 'custom' = 'main';

  static get observedAttributes() {
    return ['page-type'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  attributeChangedCallback(name: string, _oldValue: string, newValue: string) {
    if (name === 'page-type' && newValue) {
      this.pageType = newValue as 'main' | 'support' | 'printer' | 'custom';
      if (this.isConnected) {
        this.render();
      }
    }
  }

  connectedCallback() {
    this.render();
  }

  private getCurrentYear(): number {
    return new Date().getFullYear();
  }

  private getDateOffsetFromNowByMonth(offset_months: number): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + offset_months, 1);
  }

  private getOffsetDate(referenceDate: Date, offset: number): Date {
    return new Date(referenceDate.getFullYear(), referenceDate.getMonth() + offset, 1);
  }

  private formatMonthYearDisplay(date: Date): string {
    return `${getMonthName(date.getMonth())} ${date.getFullYear()}`;
  }

  private generatePrintLinksAroundDate(referenceDate: Date | null): string {
    let links = '';

    if (referenceDate) {
      for (let i = -2; i <= 2; i++) {
        const date = this.getOffsetDate(referenceDate, i);
        const url = formatMonthUrl(date.getMonth(), date.getFullYear());
        const label = this.formatMonthYearDisplay(date);

        if (i === 0) {
          links += `<span class="current-month">${label}</span>`;
        } else {
          links += `<a href="${url}">${label}</a>`;
        }
      }
    } else {
      for (let i = 0; i <= 2; i++) {
        const date = this.getDateOffsetFromNowByMonth(i);
        const url = formatMonthUrl(date.getMonth(), date.getFullYear());
        const label = this.formatMonthYearDisplay(date);
        links += `<a href="${url}">${label}</a>`;
      }
    }

    return links;
  }

  render() {
    if (!this.shadowRoot) return;

    let navContent = '';
    let currentDate: Date | null = null;

    if (this.pageType === 'printer') {
      const path = window.location.pathname;
      const parsed = parseCalendarUrl(path);
      if (parsed) {
        const monthIndex = MONTHS.indexOf(parsed.month);
        if (monthIndex !== -1) {
          currentDate = new Date(parsed.year, monthIndex, 1);
        }
      }
    }

    const homeUrl = localizePath('/');
    const supportUrl = localizePath('/support.html');

    navContent = `<div class="footer-links-container">
      <div class="footer-column">
        <a href="${homeUrl}">${t('nav.home')}</a>
        <a href="${supportUrl}">${t('nav.support')}</a>
      </div>
      <div class="footer-column">`;

    if (this.pageType === 'main' || this.pageType === 'support' || this.pageType === 'custom') {
      for (let i = 0; i <= 2; i++) {
        const date = this.getDateOffsetFromNowByMonth(i);
        const url = formatMonthUrl(date.getMonth(), date.getFullYear());
        const label = this.formatMonthYearDisplay(date);
        navContent += `<a href="${url}">${label}</a>`;
      }
    } else if (this.pageType === 'printer') {
      navContent += this.generatePrintLinksAroundDate(currentDate);
    }

    navContent += `</div></div>`;

    const currentYear = this.getCurrentYear();

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          font-family: var(--font-family);
          --footer-primary-color: var(--primary-color);
          --footer-text-color: var(--footer-text-color);
        }
        
        footer {
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 1px solid #eee;
          text-align: center;
        }
        
        .footer-nav {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 1.5rem;
          margin-bottom: 1rem;
        }
        
        .footer-links-container {
          display: flex;
          justify-content: center;
          gap: 2rem;
          margin-bottom: 1rem;
        }
        
        .footer-column {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.75rem;
        }
        
        .footer-nav a {
          color: var(--footer-primary-color);
          text-decoration: none;
          font-size: 0.9rem;
        }
        
        .footer-nav a:hover {
          text-decoration: underline;
        }
        
        .footer-nav .current-month {
          color: var(--footer-text-color);
          font-weight: var(--font-weight-semibold);
          font-size: 0.9rem;
        }
        
        .copyright {
          color: var(--footer-text-color);
          font-size: 0.75rem;
          margin-bottom: 1rem;
        }
        
        .copyright a {
          color: inherit;
          text-decoration: none;
        }
        
        .copyright a:hover {
          color: var(--footer-primary-color);
        }
        
        @media print {
          footer {
            display: none !important;
          }
        }
      </style>
      
      <footer>
        <nav class="footer-nav">
          ${navContent}
        </nav>
        <div class="copyright">
          &copy; ${currentYear} <a href="${homeUrl}">calendarify.online</a>. ${t('footer.rights')}
        </div>
      </footer>
    `;
  }
}

customElements.define('app-footer', AppFooter);
